import React, { useState } from 'react';

// Add CSS styles with keyframes
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  
  @keyframes slideUp {
    from { transform: translateY(20px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
  
  @keyframes slideDown {
    from { transform: translateY(-20px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
  
  @keyframes gradientShift {
    0% { background-position: 0% 50%; }
    100% { background-position: 100% 50%; }
  }
  
  @keyframes grow {
    from { transform: scale(0.8); opacity: 0; }
    to { transform: scale(1); opacity: 1; }
  }
  
  @keyframes pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.05); }
    100% { transform: scale(1); }
  }
  
  @keyframes bounce {
    0%, 20%, 50%, 80%, 100% {transform: translateY(0);}
    40% {transform: translateY(-10px);}
    60% {transform: translateY(-5px);}
  }
`;
document.head.appendChild(styleSheet);

const Review = () => {
  // State for reviews
  const [reviews, setReviews] = useState([
    {
      id: 1,
      employeeName: "John Doe",
      employeeId: "EMP-001",
      position: "Sales Executive",
      department: "Sales",
      reviewDate: "2023-10-15",
      rating: 4.5,
      status: "Completed",
      reviewer: "Sarah Johnson",
      strengths: ["Excellent communication skills", "Consistently meets targets", "Great team player"],
      improvements: ["Could improve documentation", "Needs to focus on follow-ups"],
      goals: ["Increase sales by 15% next quarter", "Mentor new team members"],
      feedback: "John has performed exceptionally well this quarter. His sales numbers are among the top in the team."
    },
    {
      id: 2,
      employeeName: "Jane Smith",
      employeeId: "EMP-002",
      position: "Marketing Specialist",
      department: "Marketing",
      reviewDate: "2023-10-10",
      rating: 3.8,
      status: "Completed",
      reviewer: "Michael Brown",
      strengths: ["Creative campaign ideas", "Strong analytical skills", "Good presentation skills"],
      improvements: ["Should work on time management", "Needs to improve reporting"],
      goals: ["Lead the new product launch campaign", "Complete advanced analytics course"],
      feedback: "Jane has shown great creativity in her campaigns. However, she needs to work on delivering reports on time."
    },
    {
      id: 3,
      employeeName: "Robert Johnson",
      employeeId: "EMP-003",
      position: "Software Developer",
      department: "Development",
      reviewDate: "2023-10-05",
      rating: 4.2,
      status: "In Progress",
      reviewer: "Lisa Anderson",
      strengths: ["Excellent coding skills", "Quick learner", "Problem solver"],
      improvements: ["Should improve documentation", "Needs to participate more in meetings"],
      goals: ["Learn new framework", "Take ownership of a module"],
      feedback: "Robert is a talented developer who consistently delivers high-quality code."
    },
    {
      id: 4,
      employeeName: "Emily Williams",
      employeeId: "EMP-004",
      position: "HR Manager",
      department: "HR",
      reviewDate: "2023-09-28",
      rating: 4.7,
      status: "Completed",
      reviewer: "David Wilson",
      strengths: ["Excellent people skills", "Great at conflict resolution", "Organized"],
      improvements: ["Should streamline processes", "Could use more data-driven approach"],
      goals: ["Implement new HR software", "Reduce hiring time by 20%"],
      feedback: "Emily has done an outstanding job in managing the HR department. Her interpersonal skills are exceptional."
    }
  ]);

  // State for filters
  const [filters, setFilters] = useState({
    status: 'All',
    department: 'All',
    rating: 'All'
  });

  // State for selected review
  const [selectedReview, setSelectedReview] = useState(reviews[0]);

  // State for new review form
  const [showForm, setShowForm] = useState(false);
  const [newReview, setNewReview] = useState({
    employeeName: "",
    employeeId: "",
    position: "",
    department: "Sales",
    rating: 0,
    strengths: "",
    improvements: "",
    goals: "",
    feedback: ""
  });

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    setFilters({
      ...filters,
      [filterType]: value
    });
  };

  // Handle input changes for new review
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewReview({
      ...newReview,
      [name]: value
    });
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    const review = {
      id: reviews.length + 1,
      employeeName: newReview.employeeName,
      employeeId: newReview.employeeId,
      position: newReview.position,
      department: newReview.department,
      reviewDate: new Date().toISOString().split('T')[0],
      rating: parseFloat(newReview.rating),
      status: "Completed",
      reviewer: "Manager Name",
      strengths: newReview.strengths.split(',').map(s => s.trim()),
      improvements: newReview.improvements.split(',').map(s => s.trim()),
      goals: newReview.goals.split(',').map(s => s.trim()),
      feedback: newReview.feedback
    };
    
    setReviews([...reviews, review]);
    setNewReview({
      employeeName: "",
      employeeId: "",
      position: "",
      department: "Sales",
      rating: 0,
      strengths: "",
      improvements: "",
      goals: "",
      feedback: ""
    });
    setShowForm(false);
  };

  // Filter reviews based on selected filters
  const filteredReviews = reviews.filter(review => {
    return (
      (filters.status === 'All' || review.status === filters.status) &&
      (filters.department === 'All' || review.department === filters.department) &&
      (filters.rating === 'All' || Math.floor(review.rating) === parseInt(filters.rating))
    );
  });

  // Get status color
  const getStatusColor = (status) => {
    switch(status) {
      case "Completed": return "#2ecc71";
      case "In Progress": return "#3498db";
      case "Pending": return "#e74c3c";
      default: return "#95a5a6";
    }
  };

  // Get department color
  const getDepartmentColor = (department) => {
    switch(department) {
      case "Sales": return "#ff6b6b";
      case "Marketing": return "#4ecdc4";
      case "Development": return "#45b7d1";
      case "HR": return "#f9c74f";
      case "Finance": return "#ff9f1c";
      default: return "#95a5a6";
    }
  };

  // Render star ratings
  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    
    for (let i = 0; i < fullStars; i++) {
      stars.push(<span key={i} style={styles.star}>&#9733;</span>);
    }
    
    if (hasHalfStar) {
      stars.push(<span key="half" style={styles.star}>&#9734;</span>);
    }
    
    for (let i = 0; i < 5 - fullStars - (hasHalfStar ? 1 : 0); i++) {
      stars.push(<span key={`empty-${i}`} style={styles.star}>&#9734;</span>);
    }
    
    return stars;
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.heading}>Employee Performance Reviews</h2>
      
      {/* Stats Overview */}
      <div style={styles.statsContainer}>
        <div style={styles.statCard}>
          <h3 style={styles.statNumber}>{reviews.length}</h3>
          <p style={styles.statLabel}>Total Reviews</p>
        </div>
        <div style={styles.statCard}>
          <h3 style={styles.statNumber}>{reviews.filter(r => r.status === "Completed").length}</h3>
          <p style={styles.statLabel}>Completed</p>
        </div>
        <div style={styles.statCard}>
          <h3 style={styles.statNumber}>{reviews.filter(r => r.status === "In Progress").length}</h3>
          <p style={styles.statLabel}>In Progress</p>
        </div>
        <div style={styles.statCard}>
          <h3 style={styles.statNumber}>
            {reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length.toFixed(1)}
          </h3>
          <p style={styles.statLabel}>Avg. Rating</p>
        </div>
      </div>

      {/* Filters */}
      <div style={styles.filtersContainer}>
        <h3 style={styles.filterHeading}>Filters</h3>
        <div style={styles.filterRow}>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Status</label>
            <select 
              value={filters.status} 
              onChange={(e) => handleFilterChange('status', e.target.value)}
              style={styles.filterSelect}
            >
              <option value="All">All Statuses</option>
              <option value="Completed">Completed</option>
              <option value="In Progress">In Progress</option>
              <option value="Pending">Pending</option>
            </select>
          </div>
          
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Department</label>
            <select 
              value={filters.department} 
              onChange={(e) => handleFilterChange('department', e.target.value)}
              style={styles.filterSelect}
            >
              <option value="All">All Departments</option>
              <option value="Sales">Sales</option>
              <option value="Marketing">Marketing</option>
              <option value="Development">Development</option>
              <option value="HR">HR</option>
              <option value="Finance">Finance</option>
            </select>
          </div>
          
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Rating</label>
            <select 
              value={filters.rating} 
              onChange={(e) => handleFilterChange('rating', e.target.value)}
              style={styles.filterSelect}
            >
              <option value="All">All Ratings</option>
              <option value="5">5 Stars</option>
              <option value="4">4 Stars</option>
              <option value="3">3 Stars</option>
              <option value="2">2 Stars</option>
              <option value="1">1 Star</option>
            </select>
          </div>

          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Actions</label>
            <button 
              onClick={() => setShowForm(!showForm)} 
              style={styles.addButton}
            >
              {showForm ? 'Cancel' : '+ New Review'}
            </button>
          </div>
        </div>
      </div>

      {/* New Review Form */}
      {showForm && (
        <div style={styles.formContainer}>
          <form onSubmit={handleSubmit} style={styles.form}>
            <h4 style={styles.formTitle}>Create New Performance Review</h4>
            
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Employee Name</label>
                <input 
                  type="text" 
                  name="employeeName" 
                  value={newReview.employeeName} 
                  onChange={handleInputChange}
                  style={styles.input}
                  required
                  placeholder="Enter employee name"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Employee ID</label>
                <input 
                  type="text" 
                  name="employeeId" 
                  value={newReview.employeeId} 
                  onChange={handleInputChange}
                  style={styles.input}
                  required
                  placeholder="Enter employee ID"
                />
              </div>
            </div>
            
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Position</label>
                <input 
                  type="text" 
                  name="position" 
                  value={newReview.position} 
                  onChange={handleInputChange}
                  style={styles.input}
                  required
                  placeholder="Enter position"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Department</label>
                <select 
                  name="department" 
                  value={newReview.department} 
                  onChange={handleInputChange}
                  style={styles.select}
                >
                  <option value="Sales">Sales</option>
                  <option value="Marketing">Marketing</option>
                  <option value="Development">Development</option>
                  <option value="HR">HR</option>
                  <option value="Finance">Finance</option>
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Rating (0-5)</label>
                <input 
                  type="number" 
                  name="rating" 
                  value={newReview.rating} 
                  onChange={handleInputChange}
                  style={styles.input}
                  min="0"
                  max="5"
                  step="0.5"
                  required
                />
              </div>
            </div>
            
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Strengths (comma separated)</label>
                <textarea 
                  name="strengths" 
                  value={newReview.strengths} 
                  onChange={handleInputChange}
                  style={styles.textarea}
                  placeholder="Enter strengths"
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Areas for Improvement (comma separated)</label>
                <textarea 
                  name="improvements" 
                  value={newReview.improvements} 
                  onChange={handleInputChange}
                  style={styles.textarea}
                  placeholder="Enter areas for improvement"
                  required
                />
              </div>
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Goals (comma separated)</label>
              <textarea 
                name="goals" 
                value={newReview.goals} 
                onChange={handleInputChange}
                style={styles.textarea}
                placeholder="Enter goals"
                required
              />
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Overall Feedback</label>
              <textarea 
                name="feedback" 
                value={newReview.feedback} 
                onChange={handleInputChange}
                style={{...styles.textarea, minHeight: '100px'}}
                placeholder="Enter overall feedback"
                required
              />
            </div>
            
            <button type="submit" style={styles.submitButton}>
              Create Review
            </button>
          </form>
        </div>
      )}

      <div style={styles.content}>
        {/* Reviews List */}
        <div style={styles.reviewsList}>
          <h3 style={styles.sectionHeading}>Performance Reviews</h3>
          
          <div style={styles.reviewsContainer}>
            {filteredReviews.map(review => (
              <div 
                key={review.id} 
                style={{
                  ...styles.reviewCard,
                  ...(selectedReview.id === review.id ? styles.activeReviewCard : {})
                }}
                onClick={() => setSelectedReview(review)}
              >
                <div style={styles.reviewHeader}>
                  <div style={styles.employeeInfo}>
                    <h4 style={styles.employeeName}>{review.employeeName}</h4>
                    <p style={styles.employeeDetails}>{review.position} • {review.department}</p>
                  </div>
                  <span style={{
                    ...styles.statusBadge,
                    backgroundColor: getStatusColor(review.status)
                  }}>
                    {review.status}
                  </span>
                </div>
                
                <div style={styles.reviewBody}>
                  <div style={styles.ratingContainer}>
                    <div style={styles.stars}>
                      {renderStars(review.rating)}
                    </div>
                    <span style={styles.ratingValue}>{review.rating}/5</span>
                  </div>
                  
                  <p style={styles.reviewDate}>Reviewed on: {review.reviewDate}</p>
                  <p style={styles.reviewer}>By: {review.reviewer}</p>
                </div>
                
                <div style={styles.reviewFooter}>
                  <span style={{
                    ...styles.departmentBadge,
                    backgroundColor: getDepartmentColor(review.department)
                  }}>
                    {review.department}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Review Details */}
        <div style={styles.reviewDetails}>
          <h3 style={styles.sectionHeading}>Review Details</h3>
          
          <div style={styles.detailCard}>
            <div style={styles.detailHeader}>
              <div>
                <h2 style={styles.detailTitle}>{selectedReview.employeeName}</h2>
                <p style={styles.detailSubtitle}>{selectedReview.position} • {selectedReview.department}</p>
                <p style={styles.detailId}>Employee ID: {selectedReview.employeeId}</p>
              </div>
              <span style={{
                ...styles.detailStatus,
                backgroundColor: getStatusColor(selectedReview.status)
              }}>
                {selectedReview.status}
              </span>
            </div>
            
            <div style={styles.ratingDetail}>
              <h4 style={styles.detailSectionTitle}>Overall Rating</h4>
              <div style={styles.ratingDetailContainer}>
                <div style={styles.starsLarge}>
                  {renderStars(selectedReview.rating)}
                </div>
                <span style={styles.ratingValueLarge}>{selectedReview.rating}/5</span>
              </div>
            </div>
            
            <div style={styles.detailSection}>
              <h4 style={styles.detailSectionTitle}>Strengths</h4>
              <ul style={styles.list}>
                {selectedReview.strengths.map((strength, index) => (
                  <li key={index} style={styles.listItem}>
                    <span style={styles.listIcon}>✓</span>
                    {strength}
                  </li>
                ))}
              </ul>
            </div>
            
            <div style={styles.detailSection}>
              <h4 style={styles.detailSectionTitle}>Areas for Improvement</h4>
              <ul style={styles.list}>
                {selectedReview.improvements.map((improvement, index) => (
                  <li key={index} style={styles.listItem}>
                    <span style={styles.listIcon}>↻</span>
                    {improvement}
                  </li>
                ))}
              </ul>
            </div>
            
            <div style={styles.detailSection}>
              <h4 style={styles.detailSectionTitle}>Goals</h4>
              <ul style={styles.list}>
                {selectedReview.goals.map((goal, index) => (
                  <li key={index} style={styles.listItem}>
                    <span style={styles.listIcon}>🎯</span>
                    {goal}
                  </li>
                ))}
              </ul>
            </div>
            
            <div style={styles.detailSection}>
              <h4 style={styles.detailSectionTitle}>Overall Feedback</h4>
              <p style={styles.feedbackText}>{selectedReview.feedback}</p>
            </div>
            
            <div style={styles.detailMeta}>
              <p style={styles.metaText}>Reviewed by: {selectedReview.reviewer}</p>
              <p style={styles.metaText}>Date: {selectedReview.reviewDate}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Inline CSS styles with gradients and animations
const styles = {
  container: {
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    padding: '20px',
    backgroundColor: '#f5f7f9',
    minHeight: '100vh',
    animation: 'fadeIn 0.5s ease-in-out'
  },
  heading: {
    color: '#2c3e50',
    marginBottom: '25px',
    fontSize: '28px',
    fontWeight: '600',
    background: 'linear-gradient(90deg, #3498db, #2c3e50)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    animation: 'gradientShift 3s infinite alternate'
  },
  statsContainer: {
    display: 'flex',
    gap: '20px',
    marginBottom: '30px',
    flexWrap: 'wrap'
  },
  statCard: {
    flex: '1',
    minWidth: '180px',
    background: 'linear-gradient(135deg, #3498db, #2c3e50)',
    color: 'white',
    borderRadius: '15px',
    padding: '20px',
    textAlign: 'center',
    boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    animation: 'slideUp 0.5s ease-out'
  },
  statNumber: {
    fontSize: '32px',
    fontWeight: '700',
    margin: '0 0 10px 0',
    textShadow: '0 2px 4px rgba(0,0,0,0.2)'
  },
  statLabel: {
    margin: '0',
    fontSize: '14px',
    fontWeight: '600',
    opacity: '0.9'
  },
  filtersContainer: {
    backgroundColor: 'white',
    borderRadius: '15px',
    padding: '20px',
    marginBottom: '20px',
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
  },
  filterHeading: {
    margin: '0 0 15px 0',
    color: '#2c3e50',
    fontSize: '18px'
  },
  filterRow: {
    display: 'flex',
    gap: '15px',
    flexWrap: 'wrap'
  },
  filterGroup: {
    flex: '1',
    minWidth: '150px'
  },
  filterLabel: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '600',
    color: '#34495e',
    fontSize: '14px'
  },
  filterSelect: {
    width: '100%',
    padding: '10px',
    border: '1px solid #ddd',
    borderRadius: '8px',
    fontSize: '14px',
    backgroundColor: 'white'
  },
  addButton: {
    padding: '10px 20px',
    background: 'linear-gradient(135deg, #2ecc71, #27ae60)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
    transition: 'transform 0.2s, box-shadow 0.2s',
    marginTop: '22px'
  },
  formContainer: {
    backgroundColor: 'white',
    borderRadius: '15px',
    padding: '25px',
    marginBottom: '20px',
    boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
    animation: 'slideDown 0.3s ease-out'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px'
  },
  formTitle: {
    margin: '0 0 15px 0',
    color: '#2c3e50',
    fontSize: '18px'
  },
  formRow: {
    display: 'flex',
    gap: '15px',
    flexWrap: 'wrap'
  },
  formGroup: {
    flex: '1',
    minWidth: '200px',
    display: 'flex',
    flexDirection: 'column'
  },
  label: {
    marginBottom: '8px',
    fontWeight: '600',
    color: '#34495e',
    fontSize: '14px'
  },
  input: {
    padding: '12px',
    border: '1px solid #ddd',
    borderRadius: '8px',
    fontSize: '14px',
    transition: 'border-color 0.3s, box-shadow 0.3s'
  },
  select: {
    padding: '12px',
    border: '1px solid #ddd',
    borderRadius: '8px',
    fontSize: '14px',
    backgroundColor: 'white'
  },
  textarea: {
    padding: '12px',
    border: '1px solid #ddd',
    borderRadius: '8px',
    fontSize: '14px',
    minHeight: '60px',
    resize: 'vertical',
    fontFamily: 'inherit'
  },
  submitButton: {
    padding: '14px',
    background: 'linear-gradient(135deg, #3498db, #2980b9)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s',
    marginTop: '10px'
  },
  content: {
    display: 'flex',
    gap: '30px',
    alignItems: 'flex-start'
  },
  reviewsList: {
    flex: '1'
  },
  reviewDetails: {
    flex: '1',
    maxWidth: '600px'
  },
  sectionHeading: {
    color: '#2c3e50',
    margin: '0 0 20px 0',
    fontSize: '20px',
    fontWeight: '600'
  },
  reviewsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px'
  },
  reviewCard: {
    backgroundColor: 'white',
    borderRadius: '15px',
    padding: '20px',
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s',
    animation: 'slideUp 0.5s ease-out'
  },
  activeReviewCard: {
    transform: 'translateY(-2px)',
    boxShadow: '0 10px 20px rgba(0,0,0,0.15)',
    borderLeft: '4px solid #3498db'
  },
  reviewHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '15px'
  },
  employeeInfo: {
    flex: '1'
  },
  employeeName: {
    margin: '0 0 5px 0',
    color: '#2c3e50',
    fontSize: '18px',
    fontWeight: '600'
  },
  employeeDetails: {
    margin: '0',
    color: '#7f8c8d',
    fontSize: '14px'
  },
  statusBadge: {
    padding: '5px 10px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
    color: 'white'
  },
  reviewBody: {
    marginBottom: '15px'
  },
  ratingContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '10px'
  },
  stars: {
    display: 'flex',
    gap: '2px'
  },
  star: {
    color: '#f39c12',
    fontSize: '16px'
  },
  ratingValue: {
    fontSize: '14px',
    color: '#7f8c8d',
    fontWeight: '600'
  },
  reviewDate: {
    margin: '0 0 5px 0',
    fontSize: '12px',
    color: '#7f8c8d'
  },
  reviewer: {
    margin: '0',
    fontSize: '12px',
    color: '#7f8c8d'
  },
  reviewFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  departmentBadge: {
    padding: '5px 10px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
    color: 'white'
  },
  detailCard: {
    backgroundColor: 'white',
    borderRadius: '15px',
    padding: '25px',
    boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
    animation: 'fadeIn 0.5s ease-out'
  },
  detailHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '20px',
    paddingBottom: '15px',
    borderBottom: '1px solid #eee'
  },
  detailTitle: {
    margin: '0 0 5px 0',
    color: '#2c3e50',
    fontSize: '24px',
    fontWeight: '600'
  },
  detailSubtitle: {
    margin: '0 0 5px 0',
    color: '#7f8c8d',
    fontSize: '16px'
  },
  detailId: {
    margin: '0',
    color: '#7f8c8d',
    fontSize: '14px'
  },
  detailStatus: {
    padding: '5px 15px',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: '600',
    color: 'white'
  },
  ratingDetail: {
    marginBottom: '20px',
    paddingBottom: '15px',
    borderBottom: '1px solid #eee'
  },
  detailSectionTitle: {
    margin: '0 0 15px 0',
    color: '#2c3e50',
    fontSize: '18px'
  },
  ratingDetailContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px'
  },
  starsLarge: {
    display: 'flex',
    gap: '2px'
  },
  ratingValueLarge: {
    fontSize: '18px',
    color: '#2c3e50',
    fontWeight: '600'
  },
  detailSection: {
    marginBottom: '20px',
    paddingBottom: '15px',
    borderBottom: '1px solid #eee'
  },
  list: {
    margin: '0',
    paddingLeft: '0'
  },
  listItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    marginBottom: '10px',
    color: '#34495e'
  },
  listIcon: {
    fontSize: '16px',
    fontWeight: 'bold'
  },
  feedbackText: {
    margin: '0',
    color: '#34495e',
    lineHeight: '1.6'
  },
  detailMeta: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '20px',
    paddingTop: '15px',
    borderTop: '1px solid #eee'
  },
  metaText: {
    margin: '0',
    fontSize: '14px',
    color: '#7f8c8d'
  }
};

// Add hover effects
Object.assign(styles.statCard, {
  ':hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 15px 30px rgba(0,0,0,0.15)'
  }
});

Object.assign(styles.addButton, {
  ':hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 12px rgba(0,0,0,0.15)'
  }
});

Object.assign(styles.submitButton, {
  ':hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 12px rgba(0,0,0,0.15)'
  }
});

Object.assign(styles.reviewCard, {
  ':hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 12px rgba(0,0,0,0.15)'
  }
});

export default Review;
